import posthog from 'posthog-js';

declare const process: {
    env: {
        POSTHOG_PROJECT_TOKEN?: string;
        POSTHOG_HOST?: string;
        POSTHOG_UI_HOST?: string;
        POSTHOG_STANDALONE_MODE?: string | boolean;
    };
};

let initialized = false;

function readSetting(setting: string | boolean | undefined): string {
    return typeof setting === 'string' ? setting.trim() : '';
}

function isStandaloneMode(): boolean {
    try {
        const mode = process.env.POSTHOG_STANDALONE_MODE;
        return mode === true || readSetting(mode).toLowerCase() === 'true';
    } catch {
        return false;
    }
}

function getPostHogSettings(): { token: string; apiHost: string; uiHost: string } | null {
    const token = readSetting(process.env.POSTHOG_PROJECT_TOKEN);
    const apiHost = readSetting(process.env.POSTHOG_HOST);
    const uiHost = readSetting(process.env.POSTHOG_UI_HOST);
    if (!token || !apiHost || !uiHost) {
        return null;
    }
    return { token, apiHost, uiHost };
}

/**
 * Idempotent client init. No-ops when configuration is missing or when this
 * standalone/local build deliberately uses the no-op analytics client.
 * Must never throw into the UI.
 */
export function ensurePostHogInitialized(): boolean {
    try {
        if (typeof window === 'undefined' || isStandaloneMode()) {
            return false;
        }
        if (initialized) {
            return true;
        }
        const settings = getPostHogSettings();
        if (!settings) {
            return false;
        }
        initialized = true;
        posthog.init(settings.token, {
            api_host: settings.apiHost,
            ui_host: settings.uiHost,
            person_profiles: 'identified_only',
            capture_pageview: 'history_change',
            capture_pageleave: true,
            autocapture: true,
        });
        return true;
    } catch {
        initialized = false;
        return false;
    }
}

function isPersistedUserId(distinctId: string): boolean {
    return /^[0-9]+$/.test(distinctId);
}

/**
 * PostHog aliases an anonymous distinct id into the next identify. A previous
 * logged-in id must be cleared first, or a linked-account switch keeps the
 * old cookie and merges two users.
 */
function shouldResetIdentifiedUser(nextDistinctId: string): boolean {
    const currentDistinctId = String(posthog.get_distinct_id() ?? '').trim();
    if (!currentDistinctId || currentDistinctId === nextDistinctId) {
        return false;
    }
    if (posthog.get_property('$user_state') === 'identified') {
        return true;
    }
    return isPersistedUserId(currentDistinctId);
}

function resetIdentifiedUserIfSwitching(nextDistinctId: string): void {
    try {
        if (shouldResetIdentifiedUser(nextDistinctId)) {
            posthog.reset();
        }
    } catch {
        // Still identify below. Analytics must not throw into the UI.
    }
}

/**
 * Identifies the logged-in user by stable id only.
 * Never send passwords, tokens, or other profile fields.
 */
export function identifyPostHogUser(userId: string | number | null | undefined): void {
    try {
        const distinctId = userId == null ? '' : String(userId).trim();
        if (!distinctId || !ensurePostHogInitialized()) {
            return;
        }
        resetIdentifiedUserIfSwitching(distinctId);
        posthog.identify(distinctId);
    } catch {
        // Never let analytics throw into the UI
    }
}
